import AppContext from 'App/AppContext';
import { IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { CopilotMarkdown } from 'components/Copilot/Panel/Conversation/CopilotMarkdown';
import GenericModal from 'components/Dialogs/GenericModal';
import InfoMessage from 'components/Notification/InfoMessage';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import {
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import { EvidenceTaskAdvice, getAuditCheckStatusText } from 'models/auditCriteria';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetEvidenceTaskAdvice } from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import { toLocaleDateTime } from 'utils/datetime';

interface IEvidenceTaskAdviceModalProps {
  isOpen: boolean;
  entityType: number;
  entityId: number;
  taskId: number;
  onClose: () => void;
}

// The stored AI advice on one evidence task: the task's evidence judged against the entity's one-time
// criteria, as the report the single-task audit writes. Advice only: completing or archiving the task
// is what verifies a criterion.
const EvidenceTaskAdviceModal = (props: IEvidenceTaskAdviceModalProps) => {
  const { t } = useTranslation(['criteria', 'translation']);
  const appContext = useContext(AppContext);
  const [advice, setAdvice] = useState<EvidenceTaskAdvice | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!props.isOpen) return;

    const load = async () => {
      try {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        setAdvice(await apiGetEvidenceTaskAdvice(props.entityType, props.entityId, props.taskId, accessToken));
      } catch (err) {
        appContext.setError(err);
        props.onClose();
      } finally {
        setIsLoading(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, props.entityType, props.entityId, props.taskId]);

  const statusText = getAuditCheckStatusText(advice?.overallStatus, t);

  return (
    <GenericModal
      isOpen={props.isOpen}
      isBlocking
      titleSeparator
      onClose={props.onClose}
      title={
        advice?.taskName ? `${t('criteria:Tasks.Advice.Title')} - ${advice.taskName}` : t('criteria:Tasks.Advice.Title')
      }
      isActionPending={isLoading}
      height={700}
      width={800}
      maxWidth={'min(90vw, 800px)'}
      maxHeight={'90vh'}
    >
      <IPStack verticalFill tokens={globalStackTokensGapMedium} styles={{ root: { paddingTop: 12, minHeight: 0 } }}>
        <IPStack.Item>
          <InfoMessage message={t('criteria:Tasks.Advice.Disclaimer')} />
        </IPStack.Item>
        {advice && (
          <IPStack.Item>
            <IPStack horizontal wrap verticalAlign="center" tokens={globalStackTokensGapSmall}>
              {statusText && (
                <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapExtraSmall}>
                  <IPText>{t('criteria:Tasks.Advice.Overall')}</IPText>
                  <IPText styles={globalTextStylesBold}>{statusText}</IPText>
                </IPStack>
              )}
              {advice.generatedOn && (
                <IPText variant="small">
                  {t('criteria:Tasks.Advice.GeneratedOn', { date: toLocaleDateTime(advice.generatedOn) })}
                </IPText>
              )}
            </IPStack>
          </IPStack.Item>
        )}
        <ScrollableStackItem>
          {isLoading && !advice && <IPSpinner size={IPSpinnerSize.large} />}
          {/* the report carries an HTML disclaimer span for the copilot panel; skipped here, the
              message bar above says the same. GFM renders its criteria table. */}
          {advice && <CopilotMarkdown content={advice.markdown} withTableStyles skipHtml />}
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};

export default EvidenceTaskAdviceModal;
